<template>
  <div id="gatewaysList" class="row">
    <div class="col-xl-6 mb-3">
      <card :title="$t('a-gateways:filter')" :visible="false" collapsible>
        <div slot="card-body">
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="filterEui">{{ $t('a-gateways:eui') | capitalize }}</label>
              <input id="filterEui"
                     v-model="filterEui"
                     v-validate="'text'"
                     :data-vv-as="$t('a-gateways:eui')"
                     :class="{ 'is-invalid': errors.has('filterEui') }"
                     type="text"
                     name="filterEui"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('filterEui') }}</div>
            </div>

            <div class="form-group col-md-6">
              <label for="filterName">{{ $t('a-gateways:name') | capitalize }}</label>
              <input id="filterName"
                     v-model="filterName"
                     v-validate="'text'"
                     :data-vv-as="$t('a-gateways:name')"
                     :class="{ 'is-invalid': errors.has('filterName') }"
                     type="text"
                     name="filterName"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('filterName') }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="filterBrand">{{ $t('a-gateways:brand') | capitalize }}</label>
              <select id="filterBrand" v-model="filterBrand" name="filterBrand" class="form-control">
                <option value="" />
                <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name | gatewayBrand }}</option>
              </select>
            </div>

            <div class="form-group col-md-6">
              <label for="filterFleet">{{ $t('a-gateways:fleet') | capitalize }}</label>
              <select id="filterFleet" v-model="filterFleet" name="filterFleet" class="form-control">
                <option value="" />
                <option v-for="(f, indexFleet) in $store.getters.allFleets" :key="indexFleet" :value="f">{{ f.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <div slot="card-footer">
          <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('a-gateways:search') | capitalize }}</button>
        </div>
      </card>

      <grid id="gatewaysGrid"
            :title="$t('a-gateways:gateways')"
            :columns="columns"
            :paginated-dto="gateways"
            :manual-export="exportAllGateways"
            :custom-export-label="$t('a-gateways:exportAll')"
            @load="filter"
      >
        <template #header-right>
          <progress-circular v-show="exportAllProgress" :progress="exportAllProgress" />
          <!-- Need a link to click on when we get the csv url, can't use download attribute because of IE11 -->
          <a id="exportAllUrl" :href="exportAllUrl" class="hidden" />
        </template>

        <div slot="eui" slot-scope="props" :data-gateway-id="props.dto.id" :data-gateway-eui="props.dto.eui" class="d-flex">
          <div class="mr-2"><i class="icon-gateway no-pointer" /></div>
          {{ props.dto.eui }}
        </div>

        <span slot="name" slot-scope="props" class="max-width-cell">{{ props.dto.name }}</span>

        <div slot="actions" slot-scope="props" :data-gateway-id="props.dto.id" class="text-right">
          <i :id="'deleteGateway-' + props.dto.id" class="icon-trash" @click="confirmDeleteGateway(props.dto)" />
        </div>
      </grid>

      <confirm id="deleteGateway"
               v-model="showDeleteGatewayConfirm"
               :message="$t('a-gateways:confirmDelete', { gateway: gatewayToDelete.eui })"
               @confirm:validate="deleteGateway"
      />

      <floating-button :click-handler="() => { showGatewayForm=true }" icon-classes="icon-add" />
      <gateways-form v-model="showGatewayForm" @success="filter" />
    </div>

    <div class="col-xl-6">
      <gateways-import />
    </div>
  </div>
</template>

<script>
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import gatewayBrand from '@/filters/gatewayBrand'

import GatewaysImport from './GatewaysImport'
import GatewaysForm from './GatewaysForm'

import exportAllGateways from '@/mixins/exportAllGateways'

export default {
  titleKey: 'administrationGateways',

  components: { GatewaysImport, GatewaysForm },

  mixins: [exportAllGateways],

  data () {
    return {
      // filter
      filterEui: '',
      filterName: '',
      filterBrand: '',
      filterFleet: '',
      // list
      gateways: {},
      showDeleteGatewayConfirm: false,
      gatewayToDelete: {},
      brands: [],
      // Add/edit gateway
      showGatewayForm: false
    }
  },

  computed: {
    columns () {
      return [
        { text: this.$t('a-gateways:eui'), slot: 'eui', sort: 'eui' },
        { text: this.$t('a-gateways:name'), slot: 'name', sort: 'name' },
        { text: this.$t('a-gateways:brand'), formatter: g => gatewayBrand(g.brand.name), sort: 'brand.name' },
        { text: this.$t('a-gateways:fleet'), formatter: gateway => (gateway.fleet ? gateway.fleet.name : '') },
        { slot: 'actions' }
      ]
    }
  },

  created () {
    this.filter()
    this.getGatewayBrands()
    this.$store.dispatch('getAllFleets')

    eventBus.$on('async-bulk:success', this.filter)
  },

  destroyed () {
    eventBus.$off('async-bulk:success', this.filter)
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.filterEui) search = this.$utils.addSearch(search, 'like', '*' + this.filterEui + '*', 'eui')
      if (this.filterName) search = this.$utils.addSearch(search, 'like', '*' + this.filterName + '*', 'name')
      if (this.filterBrand) search = this.$utils.addSearch(search, 'eq', this.filterBrand, 'brand.id')
      if (this.filterFleet) search = this.$utils.addSearch(search, 'eq', this.filterFleet.id, 'fleet.id')

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
      this.searchString = options.search

      // Get filtered data
      this.getGateways(options)
    },

    async getGatewayBrands () {
      let error, response
      [error, response] = await to(this.$api.getGatewayBrands(this.$store.getters.application), { sort: '+name' })
      if (error) return

      this.brands = response.data
    },

    async getGateways (options) {
      const params = this.$utils.getParams({ sort: '+eui' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getGateways(this.$store.getters.application, params))
      if (error || !response) return

      this.gateways = response.data
    },

    confirmDeleteGateway (gateway) {
      this.showDeleteGatewayConfirm = true
      this.gatewayToDelete = gateway
    },

    async deleteGateway () {
      let error
      [error] = await to(this.$api.deleteGateway(this.gatewayToDelete))
      if (error) return

      // Update selection
      this.$store.commit('removeGatewayFromSelection', this.gatewayToDelete)
      // Success
      eventBus.$emit('message-success', this.$t('a-gateways:deleteSuccess', { gateway: this.gatewayToDelete.eui }))
      // Refresh list
      this.filter()
    }
  }
}
</script>
